import { useState, useEffect } from 'react';
import { LuPlus, LuX, LuCheck } from 'react-icons/lu';
import TraderLayout from '../../components/TraderLayout/TraderLayout';
import AddBatchDrawer from '../../components/AddBatchDrawer/AddBatchDrawer';
import BatchDetailDrawer from '../../components/BatchDetailDrawer/BatchDetailDrawer';
import { fetchBatches } from '../../lib/directus';
import styles from './TraderDashboard.module.css';
import tStyles from './TraderStatusBadge.module.css';

function parseQualitySpecs(str) {
  let moisture = '', foreign = '', broken = '';
  if (!str) return { moisture, foreign, broken };
  str.split('\n').forEach(line => {
    const [key, val] = line.split(':').map(s => s.trim());
    if (!key || !val) return;
    const k = key.toLowerCase();
    if (k.includes('moisture')) moisture = val;
    else if (k.includes('foreign')) foreign = val;
    else if (k.includes('broken')) broken = val;
  });
  return { moisture, foreign, broken };
}

function mapBatch(b) {
  const { moisture, foreign, broken } = parseQualitySpecs(b.quality_specifications);
  return {
    id: b.id,
    batchId: '#' + String(b.id).slice(-6).toUpperCase(),
    commodity: b.commodity || '',
    quantity: b.quantity != null ? `${b.quantity}${b.unit_of_measure ? ' ' + b.unit_of_measure : ''}` : '',
    rawQuantity: parseFloat(b.quantity) || 0,
    grade: b.grade || '',
    status: b.status || 'Pending',
    warehouse: b.location || '',
    amount: b.price ? `R${b.price}` : '',
    rawPrice: parseFloat(b.price) || 0,
    moisture,
    foreign,
    broken,
    buyer: { company: '', trader: '', rating: 0, trades: 0 },
    escrowAmount: '',
    escrowDate: '',
  };
}

function fmtQty(mt) {
  if (mt >= 1000) return `${(mt / 1000).toFixed(1)}K MT`;
  return `${mt} MT`;
}

function fmtRevenue(n) {
  if (n >= 1_000_000) return `R${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `R${(n / 1_000).toFixed(0)}K`;
  return `R${n.toLocaleString()}`;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function TraderStatusBadge({ status }) {
  const map = {
    'Live':             tStyles.tLive,
    'Pending':          tStyles.tPending,
    'Sold':             tStyles.tSold,
    'Awaiting Payment': tStyles.tAwaiting,
    'Locked':           tStyles.tLocked,
  };
  return <span className={`${tStyles.tBadge} ${map[status] || ''}`}>{status}</span>;
}

function SuccessModal({ message, onClose }) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}><LuX size={18} /></button>
        <div className={styles.successIcon}><LuCheck size={32} color="white" /></div>
        <p className={styles.successMsg}>{message}</p>
      </div>
    </div>
  );
}

export default function TraderDashboard() {
  const [addBatchOpen, setAddBatchOpen] = useState(false);
  const [activeBatch, setActiveBatch]   = useState(null);
  const [successMsg, setSuccessMsg]     = useState('');
  const [inventory, setInventory]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAll, setShowAll]           = useState(false);

  useEffect(() => {
    fetchBatches()
      .then(data => setInventory(data.map(mapBatch)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function refreshInventory() {
    fetchBatches()
      .then(data => setInventory(data.map(mapBatch)))
      .catch(console.error);
  }

  return (
    <TraderLayout>
      <div className={styles.page}>
        <AddBatchDrawer
          isOpen={addBatchOpen}
          onClose={() => setAddBatchOpen(false)}
          onSubmit={() => {
            setAddBatchOpen(false);
            setSuccessMsg('Batch submitted successfully');
            refreshInventory();
          }}
        />
        <BatchDetailDrawer
          batch={activeBatch}
          onClose={() => setActiveBatch(null)}
          onAcceptLock={() => {
            setActiveBatch(null);
            setSuccessMsg('Stock accepted and locked');
          }}
          onPaused={() => {
            refreshInventory();
            setSuccessMsg('Listing paused');
          }}
        />

        {successMsg && <SuccessModal message={successMsg} onClose={() => setSuccessMsg('')} />}

        {/* Page header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Dashboard</h1>
          {inventory.length > 0 && (
            <button className={styles.addBtn} onClick={() => setAddBatchOpen(true)}>
              <LuPlus size={16} /> Add new batch
            </button>
          )}
        </div>

        {loading && <p className={styles.loadingText}>Loading...</p>}

        {!loading && inventory.length === 0 && (
          <div className={styles.emptyState}>
            <img src="/empty-mailbox.png" alt="" className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>You have no inventory yet</p>
            <p className={styles.emptySubtitle}>All your inventories will<br />show up here</p>
            <button className={styles.emptyAddBtn} onClick={() => setAddBatchOpen(true)}>
              <LuPlus size={15} /> Add new batch
            </button>
          </div>
        )}

        {!loading && inventory.length > 0 && (
          <>
            {/* Stat cards */}
            {(() => {
              const totalStock = inventory.reduce((s, b) => s + b.rawQuantity, 0);
              const listed     = inventory.filter(b => b.status === 'Live').length;
              const pending    = inventory.filter(b => b.status === 'Pending').length;
              const revenue    = inventory
                .filter(b => b.status === 'Sold')
                .reduce((s, b) => s + b.rawPrice, 0);
              return (
                <div className={styles.statsRow}>
                  <div className={styles.statCard}>
                    <p className={styles.statValue}>{fmtQty(totalStock)}</p>
                    <p className={styles.statLabel}>total stock</p>
                  </div>
                  <div className={styles.statCard}>
                    <p className={styles.statValue}>{pad2(listed)}</p>
                    <p className={styles.statLabel}>listed</p>
                  </div>
                  <div className={styles.statCard}>
                    <p className={styles.statValue}>{pad2(pending)}</p>
                    <p className={styles.statLabel}>pending</p>
                  </div>
                  <div className={styles.statCard}>
                    <p className={styles.statValue}>{fmtRevenue(revenue)}</p>
                    <p className={styles.statLabel}>revenue</p>
                  </div>
                </div>
              );
            })()}

            {/* Recent inventory */}
            <div className={styles.inventorySection}>
              <div className={styles.inventoryHeader}>
                <p className={styles.sectionTitle}>Recent inventory</p>
                {inventory.length > 6 && (
                  <button className={styles.seeAll} onClick={() => setShowAll(v => !v)}>
                    {showAll ? 'Show less' : 'See all'}
                  </button>
                )}
              </div>
              {(showAll ? inventory : inventory.slice(0, 6)).map(item => (
                <div key={item.id} className={styles.inventoryItem} onClick={() => setActiveBatch(item)}>
                  <div>
                    <p className={styles.batchId}>{item.batchId}</p>
                    <p className={styles.itemName}>{item.commodity}</p>
                    <p className={styles.itemMeta}>{item.quantity} • {item.grade}</p>
                  </div>
                  <TraderStatusBadge status={item.status} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </TraderLayout>
  );
}
